import React, { useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  Cell,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  USAGE_CATEGORY_LABELS,
  UsageCategory,
  UsageGroupComparisonDTO,
} from "../../../api/admin/types";
import { CATEGORY_COLORS, visibleCategories } from "../shared/constants";

interface AgencySmallMultiplesProps {
  groups: UsageGroupComparisonDTO[];
  title?: string;
  subtitle?: string;
  showEpic?: boolean;
}

// Recharts' built-in `position: "right"` always places the label at the bar's
// +x edge, which for a negative bar lands at the origin and visually collides
// with the y-axis labels. This component flips the side based on value sign.
const ValueLabel = (props: any) => {
  const { x, y, width, height, value } = props;
  if (value == null || value === 0) return null;
  const isPositive = value > 0;
  const labelX = isPositive ? x + width + 4 : x - 4;
  const anchor = isPositive ? "start" : "end";
  return (
    <text
      x={labelX}
      y={y + height / 2}
      dy={4}
      textAnchor={anchor}
      fontSize={10}
      fontWeight={600}
      fill="#374151"
    >
      {value}
    </text>
  );
};

const AgencySmallMultiples: React.FC<AgencySmallMultiplesProps> = ({
  groups,
  title,
  subtitle,
  showEpic = true,
}) => {
  const [limit, setLimit] = useState<number>(12);

  const cats = visibleCategories(showEpic);

  const sorted = useMemo(
    () => [...groups].sort((a, b) => Math.abs(b.delta.total) - Math.abs(a.delta.total)),
    [groups]
  );

  const visible = sorted.slice(0, limit);

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 8,
        padding: 20,
        width: "100%",
        maxWidth: 1200,
        boxSizing: "border-box",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>
            {title ?? "Period-over-period change by category"}
          </div>
          {subtitle ? <div style={{ fontSize: 12, color: "#6b7280" }}>{subtitle}</div> : null}
        </div>
        {sorted.length > limit ? (
          <button
            onClick={() => setLimit((l) => l + 12)}
            style={{
              padding: "6px 14px",
              fontSize: 13,
              borderRadius: 4,
              border: "1px solid #d1d5db",
              background: "#f9fafb",
              cursor: "pointer",
            }}
          >
            Show more ({sorted.length - limit} remaining)
          </button>
        ) : null}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))",
          gap: 16,
        }}
      >
        {visible.map((g) => {
          const data = cats.map((c) => ({
            cat: c,
            label: USAGE_CATEGORY_LABELS[c],
            delta: g.delta.totals[c] ?? 0,
          })).filter((d) => d.delta !== 0);
          return (
            <div
              key={g.id}
              style={{
                border: "1px solid #f1f5f9",
                borderRadius: 8,
                padding: 12,
                background: "#fcfcfd",
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 14 }}>{g.label}</div>
              <div style={{ width: "100%", height: 240 }}>
                <ResponsiveContainer>
                  <BarChart
                    data={data}
                    layout="vertical"
                    margin={{ top: 8, right: 48, left: 4, bottom: 8 }}
                  >
                    <XAxis type="number" tick={{ fontSize: 10 }} />
                    <YAxis
                      type="category"
                      dataKey="label"
                      width={140}
                      tick={{ fontSize: 10 }}
                      interval={0}
                    />
                    <Tooltip />
                    <ReferenceLine x={0} stroke="#9ca3af" />
                    <Bar dataKey="delta" isAnimationActive={false}>
                      {data.map((d, i) => (
                        <Cell key={i} fill={CATEGORY_COLORS[d.cat as UsageCategory]} />
                      ))}
                      <LabelList dataKey="delta" content={ValueLabel} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AgencySmallMultiples;
